import { useApiFetch } from '~/composables/useApiFetch';
import type {
  ArchiveNotificationsRequest,
  ArchiveNotificationsResponse,
  DeleteNotificationsRequest,
  DeleteNotificationsResponse,
  GetNotificationsParams,
  MarkReadRequest,
  MarkReadResponse,
  Notification,
  NotificationListResponse,
  NotificationSettings,
  UnreadCountResponse,
  UpdateNotificationSettingsRequest,
} from '~/types/notifications';
import { unwrapEnvelope } from '~/utils/apiEnvelope';

/** V2 meta 格式 */
interface V2ListMeta {
  page?: number;
  pageSize?: number;
  total?: number;
  totalPages?: number;
}

/**
 * 將 API 回傳解析為 NotificationListResponse
 * 相容 V1：{ data: { notifications, pagination } } 或 { notifications, pagination }
 * 相容 V2：{ data: [...], meta: { page, pageSize, total, totalPages } }
 */
function parseNotificationsResponse(body: unknown): NotificationListResponse {
  if (body === null || typeof body !== 'object') {
    return {
      notifications: [],
      pagination: { page: 1, page_size: 20, total_count: 0, total_pages: 0 },
    };
  }

  const obj = body as Record<string, unknown>;

  // V2 格式：{ data: [...], meta: {...} }
  if ('data' in obj && Array.isArray(obj.data)) {
    const list = obj.data as Notification[];
    const meta = ('meta' in obj ? obj.meta : null) as V2ListMeta | null;
    const page = meta?.page ?? 1;
    const pageSize = meta?.pageSize ?? list.length;
    const total = meta?.total ?? list.length;
    const totalPages = meta?.totalPages ?? (total > 0 ? Math.ceil(total / pageSize) : 0);
    return {
      notifications: list,
      pagination: {
        page,
        page_size: pageSize,
        total_count: total,
        total_pages: totalPages,
      },
    };
  }

  // V1 格式：unwrap 後應有 notifications + pagination
  const data = 'data' in obj ? obj.data : obj;
  if (data !== null && typeof data === 'object') {
    const inner = data as Record<string, unknown>;
    if ('notifications' in inner && 'pagination' in inner) {
      const pag = inner.pagination as {
        page?: number;
        page_size?: number;
        total_count?: number;
        total_pages?: number;
      };
      return {
        notifications: Array.isArray(inner.notifications)
          ? (inner.notifications as Notification[])
          : [],
        pagination: {
          page: pag?.page ?? 1,
          page_size: pag?.page_size ?? 20,
          total_count: pag?.total_count ?? 0,
          total_pages: pag?.total_pages ?? 0,
        },
      };
    }
  }

  return {
    notifications: [],
    pagination: { page: 1, page_size: 20, total_count: 0, total_pages: 0 },
  };
}

/**
 * 獲取通知列表
 */
export async function getNotificationsApi(
  params: GetNotificationsParams = {}
): Promise<NotificationListResponse> {
  const queryParams = new URLSearchParams();
  if (params.type) queryParams.append('type', params.type);
  if (params.is_read !== undefined) queryParams.append('is_read', String(params.is_read));
  if (params.is_archived !== undefined)
    queryParams.append('is_archived', String(params.is_archived));
  if (params.page) queryParams.append('page', String(params.page));
  if (params.page_size) queryParams.append('page_size', String(params.page_size));

  const queryString = queryParams.toString();
  const url = queryString ? `/notifications?${queryString}` : '/notifications';
  const response = await useApiFetch(url, {
    method: 'GET',
    requireAuth: true,
  });
  const body = await response.json();
  return parseNotificationsResponse(body);
}

/**
 * 獲取未讀通知數量
 */
export async function getUnreadCountApi(): Promise<UnreadCountResponse> {
  const response = await useApiFetch('/notifications/unread-count', {
    method: 'GET',
    requireAuth: true,
  });

  return unwrapEnvelope<UnreadCountResponse>(await response.json());
}

/**
 * 標記通知為已讀
 */
export async function markNotificationsReadApi(
  payload: MarkReadRequest
): Promise<MarkReadResponse> {
  const response = await useApiFetch('/notifications/read', {
    method: 'POST',
    body: JSON.stringify(payload),
    requireAuth: true,
  });

  return unwrapEnvelope<MarkReadResponse>(await response.json());
}

/**
 * 刪除通知
 */
export async function deleteNotificationsApi(
  payload: DeleteNotificationsRequest
): Promise<DeleteNotificationsResponse> {
  const response = await useApiFetch('/notifications', {
    method: 'DELETE',
    body: JSON.stringify(payload),
    requireAuth: true,
  });

  return unwrapEnvelope<DeleteNotificationsResponse>(await response.json());
}

/**
 * 歸檔通知
 */
export async function archiveNotificationsApi(
  payload: ArchiveNotificationsRequest
): Promise<ArchiveNotificationsResponse> {
  const response = await useApiFetch('/notifications/archive', {
    method: 'POST',
    body: JSON.stringify(payload),
    requireAuth: true,
  });

  return unwrapEnvelope<ArchiveNotificationsResponse>(await response.json());
}

/**
 * 獲取通知設置
 */
export async function getNotificationSettingsApi(): Promise<NotificationSettings> {
  const response = await useApiFetch('/notifications/settings', {
    method: 'GET',
    requireAuth: true,
  });

  return unwrapEnvelope<NotificationSettings>(await response.json());
}

/**
 * 更新通知設置
 */
export async function updateNotificationSettingsApi(
  payload: UpdateNotificationSettingsRequest
): Promise<NotificationSettings> {
  const response = await useApiFetch('/notifications/settings', {
    method: 'PUT',
    body: JSON.stringify(payload),
    requireAuth: true,
  });
  return unwrapEnvelope<NotificationSettings>(await response.json());
}
